/**
 * Portfolio Detail Page
 *
 * 포트폴리오 상세 페이지
 * - 리더 정보 및 포트폴리오 통계
 * - 성과 차트 (ROI, Drawdown 추이)
 * - 거래 이력
 * - 팔로우 버튼
 *
 * @see docs/COPY_TRADING_FRONTEND_PLAN.md
 */

import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  Table,
  Tag,
  Spin,
  Descriptions,
  Card,
  Row,
  Col,
  Statistic,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  UserOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { ThemeContext } from '@/theme/ThemeProvider';
import { CommonContainer } from '@/styles/Qstyle.ts';
import { useIsTablet } from '@utils/CalVW.tsx';
import { formatDecimal } from '@utils/formatDecimal';
import { getBigNumber } from '@utils/getBigNumber';

import { useCopyPortfolioStore } from '../../hooks/copyTrading/copyPortfolioStore';
import { FollowPortfolioModal } from './modal/FollowPortfolioModal';

import type {
  CopyPortfolio,
  CopyPortfolioStats,
} from '../../../types/copyTrading';
import { useTranslation } from 'react-i18next';
import { useMyProfile } from '@/app/hooks/user/myprofile';

export function PortfolioDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isTablet = useIsTablet();
  const { isDarkMode } = useContext(ThemeContext);
  const { data: myProfile } = useMyProfile();
  const { myFollows } = useCopyPortfolioStore();
  const [openFollowModal, setOpenFollowModal] = useState(false);

  const isMyFollow = (myFollows || []).some((follow) => follow.portfolio.id === Number(id));

  // Zustand Store
  const {
    selectedPortfolio,
    portfolioStatsMap,
    fetchPortfolioById,
    isLoading,
  } = useCopyPortfolioStore();

  // ==========================================
  // Effects
  // ==========================================

  useEffect(() => {
    if (id) {
      fetchPortfolioById(Number(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // fetchPortfolioById는 의존성에서 제외 (무한 루프 방지)

  // ==========================================
  // Handlers
  // ==========================================

  const handleBack = () => {
    navigate('/copy-trade');
  };

  const handleFollow = () => {
    setOpenFollowModal(true);
  };

  // ==========================================
  // Render Loading
  // ==========================================

  if (isLoading || !selectedPortfolio) {
    return (
      <CommonContainer>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      </CommonContainer>
    );
  }

  const portfolio = selectedPortfolio;
  const stats = portfolioStatsMap[portfolio.id];
  const isMyPortfolio = myProfile?.id === portfolio.leaderUserId;

  const isActive = portfolio.status === 1;
  const isPaused = portfolio.status === 2;

  // Status badge
  const statusConfig = {
    1: { text: t('active'), color: 'green' },
    2: { text: t('paused'), color: 'orange' },
    3: { text: t('closed'), color: 'red' },
  };
  const statusInfo = statusConfig[portfolio.status as 1 | 2 | 3];

  return (
    <>
      {/* Follow Modal */}
      <FollowPortfolioModal
        open={openFollowModal}
        setOpen={setOpenFollowModal}
        portfolio={portfolio}
      />

      <PageContainer>
        <ContentWrapper>
          {/* Back Button */}
          <BackButton onClick={handleBack}>
            <ArrowLeftOutlined /> {t('back')}
          </BackButton>

          {/* Header Section */}
          <HeaderSection isDark={isDarkMode}>
            <LeaderInfo>
              <Avatar
                isDark={isDarkMode}
                src={`${import.meta.env.VITE_APP_API_MEDIA_DEV_URL}/${portfolio.imageUrl}`}
              />
              <InfoContent>
                <LeaderName>{portfolio.leaderUsername}</LeaderName>
                <PortfolioName>{portfolio.portfolioName}</PortfolioName>
                <StatusBadge>
                  <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
                  <Followers>
                    <UserOutlined />{' '}
                    {portfolio.currentFollowers.toLocaleString()}{' '}
                    {t('followers')}
                  </Followers>
                </StatusBadge>
              </InfoContent>
            </LeaderInfo>
            {!isMyPortfolio ? (
              <FollowButton
                type="primary"
                size="large"
                disabled={!isActive || isMyFollow}
                onClick={handleFollow}
              >
                {isPaused ? t('paused') : isActive ? (isMyFollow ? t('following') : t('follow')) : t('closed')}
              </FollowButton>
            ) : null}
          </HeaderSection>

          {/* Key Stats Grid */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <StatsCard isDark={isDarkMode}>
                <Statistic
                  title={t('totalPnl')}
                  value={portfolio.totalPnl}
                  precision={2}
                  valueStyle={{
                    color: portfolio.totalPnl >= 0 ? '#22c55e' : '#ef4444',
                  }}
                  suffix="USDT"
                />
              </StatsCard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatsCard isDark={isDarkMode}>
                <Statistic
                  title={t('roi30d')}
                  value={stats?.roi30d || 0}
                  precision={2}
                  valueStyle={{
                    color: (stats?.roi30d || 0) >= 0 ? '#22c55e' : '#ef4444',
                  }}
                  suffix="%"
                />
              </StatsCard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatsCard isDark={isDarkMode}>
                <Statistic
                  title={t('winRate')}
                  value={stats?.winRate || 0}
                  precision={1}
                  suffix="%"
                />
              </StatsCard>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatsCard isDark={isDarkMode}>
                <Statistic
                  title={t('sharpeRatio')}
                  value={stats?.sharpeRatio || 0}
                  precision={2}
                />
              </StatsCard>
            </Col>
          </Row>

          {/* Portfolio Details */}
          <Section isDark={isDarkMode}>
            <SectionTitle>
              <TrophyOutlined /> {t('portfolioDetails')}
            </SectionTitle>
            <StyledDescriptions
              bordered
              column={{ xs: 1, sm: 2, md: 3 }}
              size="middle"
              isDark={isDarkMode}
            >
              <Descriptions.Item label={t('leader')}>
                {portfolio.leaderUsername}
              </Descriptions.Item>
              <Descriptions.Item label={t('portfolioName')}>
                {portfolio.portfolioName}
              </Descriptions.Item>
              <Descriptions.Item label={t('profitShareRate')}>
                <strong style={{ color: '#3b82f6' }}>
                  {formatDecimal(portfolio.profitShareRate, 2)}%
                </strong>
              </Descriptions.Item>
              <Descriptions.Item label={t('minCopyAmount')}>
                {formatDecimal(portfolio.minCopyAmount, 2)} USDT
              </Descriptions.Item>
              <Descriptions.Item label={t('maxFollowers')}>
                {portfolio.maxFollowers.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label={t('currentFollowers')}>
                {portfolio.currentFollowers.toLocaleString()}
              </Descriptions.Item>
              <Descriptions.Item label={t('totalTrades')}>
                {stats?.totalTrades?.toLocaleString() || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label={t('winRate')}>
                {stats?.winRate ? `${formatDecimal(stats.winRate, 1)}%` : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label={t('maxDrawdown')}>
                {stats?.maxDrawdown
                  ? `${formatDecimal(stats.maxDrawdown, 2)}%`
                  : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label={t('roi30d')}>
                <span
                  style={{
                    color: (stats?.roi30d || 0) >= 0 ? '#22c55e' : '#ef4444',
                    fontWeight: 600,
                  }}
                >
                  {stats?.roi30d
                    ? `${stats.roi30d >= 0 ? '+' : ''}${formatDecimal(stats.roi30d, 2)}%`
                    : 'N/A'}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label={t('roi90d')}>
                <span
                  style={{
                    color: (stats?.roi90d || 0) >= 0 ? '#22c55e' : '#ef4444',
                    fontWeight: 600,
                  }}
                >
                  {stats?.roi90d
                    ? `${stats.roi90d >= 0 ? '+' : ''}${formatDecimal(stats.roi90d, 2)}%`
                    : 'N/A'}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label={t('sharpeRatio')}>
                {stats?.sharpeRatio
                  ? formatDecimal(stats.sharpeRatio, 2)
                  : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label={t('createdDate')}>
                {new Date(portfolio.createdAt).toLocaleDateString()}
              </Descriptions.Item>
              <Descriptions.Item label={t('lastUpdated')}>
                {new Date(portfolio.updatedAt).toLocaleDateString()}
              </Descriptions.Item>
              <Descriptions.Item label={t('status')}>
                <Tag color={statusInfo.color}>{statusInfo.text}</Tag>
              </Descriptions.Item>
            </StyledDescriptions>
          </Section>

          {/* Risk Warning */}
          <WarningBox isDark={isDarkMode}>
            ⚠️ <strong>{t('investmentRiskWarning')}</strong>
            <br />
            {t('copyTradingRiskDescription')}
          </WarningBox>
        </ContentWrapper>
      </PageContainer>
    </>
  );
}

// ==========================================
// Styled Components
// ==========================================

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.body};
  padding: 24px 0;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  width: 100%;
`;

const BackButton = styled(Button)`
  margin-bottom: 16px;
`;

const HeaderSection = styled.div<{ isDark: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  margin-bottom: 24px;
  border-radius: 12px;
  background-color: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const LeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const Avatar = styled.img<{ isDark: boolean }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const LeaderName = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
`;

const PortfolioName = styled.div`
  font-size: 16px;
  color: ${({ theme }) => theme.text};
  opacity: 0.7;
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Followers = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FollowButton = styled(Button)`
  height: 50px;
  padding: 0 40px;
  font-size: 16px;
  font-weight: 600;
`;

const StatsCard = styled(Card)<{ isDark: boolean }>`
  background-color: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
  border-radius: 12px;

  .ant-statistic-title {
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
  }

  .ant-statistic-content {
    color: ${({ theme }) => theme.text};
  }
`;

const Section = styled.div<{ isDark: boolean }>`
  padding: 24px;
  margin-bottom: 24px;
  border-radius: 12px;
  background-color: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.text};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledDescriptions = styled(Descriptions)<{ isDark: boolean }>`
  .ant-descriptions-item-label {
    color: ${({ isDark }) => (isDark ? '#d1d5db' : '#374151')} !important;
    background-color: ${({ isDark }) =>
      isDark ? '#1f2937' : '#f9fafb'} !important;
    font-weight: 500;
  }

  .ant-descriptions-item-content {
    color: ${({ isDark }) => (isDark ? '#f3f4f6' : '#111827')} !important;
    background-color: ${({ isDark }) =>
      isDark ? '#111827' : '#fff'} !important;
  }

  .ant-descriptions-bordered .ant-descriptions-item-label,
  .ant-descriptions-bordered .ant-descriptions-item-content {
    border-color: ${({ isDark }) =>
      isDark ? '#374151' : '#e5e7eb'} !important;
  }
`;

const WarningBox = styled.div<{ isDark?: boolean }>`
  padding: 16px;
  margin-bottom: 24px;
  border-radius: 8px;
  background-color: ${({ isDark }) =>
    isDark ? 'rgba(251, 146, 60, 0.1)' : '#fef3c7'};
  color: ${({ isDark }) => (isDark ? '#fbbf24' : '#92400e')};
  font-size: 14px;
  line-height: 1.8;
  border-left: 4px solid #f59e0b;
`;
